import produce from 'utils/produce';
import { formatDateTime } from 'utils/format';
import queryString from 'utils/queryString';
import {
  apiClientGetRequest,
  apiClientPostRequest,
  apiClientDeleteRequest,
} from '../lib/backendRequest';
import { ORDER_STATUS } from './orders';

const usersApi = accessToken => ({
  async applyCredits(countryCode, promoCode) {
    try {
      await apiClientPostRequest(
        'api/v1/credits/apply',
        {
          country_alpha2: countryCode,
          promo_code: promoCode.toLowerCase(),
        },
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      return { data: {} };
    } catch (e) {
      if (e.body.message) {
        return { error: e.body.message };
      }
      return { error: 'Invalid promo code.' };
    }
  },

  /**
   * Returns { userData }
   */
  async getDetails() {
    try {
      const response = await apiClientGetRequest('api/v1/users/me', {
        Authorization: `Token ${accessToken}`,
      });

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code };
    }
  },

  async getOrders(sinceId = undefined) {
    try {
      const response = await apiClientGetRequest(
        queryString.stringifyUrl('api/v1/users/me/orders', {
          since_id: sinceId,
          limit: 25,
        }),
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      let nextPageSinceId;
      if (json.has_next_page && json.data.length > 0) {
        nextPageSinceId = json.data[json.data.length - 1].id;
      }

      const data = json.data.map(order => {
        const { items, shipments, giftnote } = order.sub_orders.reduce(
          (acc, subOrder) => {
            subOrder.items.forEach(item => {
              if (item.id === 'giftnote') {
                acc.giftnote = item;
              } else {
                acc.items.push(item);
              }
            });
            acc.shipments.push(...subOrder.shipments);
            return acc;
          },
          {
            items: [],
            shipments: [],
            giftnote: null,
          },
        );

        if (giftnote) {
          // add giftnote only one time at the end of the list, with disabled orderable option
          items.push({ ...giftnote, status: 0 });
        }

        return {
          id: order.id,
          externalId: order.external_id,
          status: order.status,
          statusText: ORDER_STATUS[`${order.status}`],
          createdAt: formatDateTime(order.created_at),
          items,
          shipments,
        };
      });

      return { data, nextPageSinceId };
    } catch (e) {
      return { error: e.code };
    }
  },

  async getSkuOrders(productId) {
    const { data, error } = await this.getOrders();

    if (data) {
      const skuOrders = [];
      data.forEach(order => {
        skuOrders.push(...order.items.filter(item => item.id === productId));
      });

      return {
        data: skuOrders,
      };
    }

    return { error };
  },

  async getOrderDetail(orderId) {
    try {
      const response = await apiClientGetRequest(`api/v1/orders/${orderId}`, {
        Authorization: `Token ${accessToken}`,
      });

      const json = await response.json();

      const data = {
        price: json.data.meta_data.payment_info?.overall,
        currency: json.data.meta_data.payment_info?.currency,
        items: json.data.meta_data.payment_info?.items ?? [],
        promoCode: json.data.meta_data.payment_info?.applied_promo_code ?? '',
        shippingAddress: json.data.meta_data.shipping_address,
      };

      return { data };
    } catch (e) {
      return { error: e.code };
    }
  },

  async getProcessedProductData(processedProductId) {
    try {
      const response = await apiClientGetRequest(`api/v1/suborders/${processedProductId}/json`, {
        Authorization: `Token ${accessToken}`,
      });

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code };
    }
  },

  async getProcessedProductMedia(processedProductId, countryCode) {
    try {
      const response = await apiClientGetRequest(
        `api/v1/media/${processedProductId}?location_country_alpha2=${countryCode}`,
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (e) {
      return { error: e.code };
    }
  },

  async getProcessedProductDataWithMedia(processedProductId, countryCode) {
    const { data: productData, error: productDataError } = await this.getProcessedProductData(
      processedProductId,
    );

    if (productDataError) {
      return { error: productDataError };
    }

    const { data: productMedia, error: productMediaError } = await this.getProcessedProductMedia(
      processedProductId,
      countryCode,
    );

    if (productMediaError) {
      return { error: productMediaError };
    }

    return {
      data: produce(productData, draft => {
        const convertPageData = page => {
          if (page.placeholders) {
            return {
              ...page,
              placeholders: page.placeholders.map(placeholder => {
                if (placeholder.type === 'photo' || placeholder.type === 'media') {
                  const mediumId = placeholder.value;
                  const medium = productMedia[mediumId];

                  if (medium) {
                    let photoUrl = medium.url;

                    if (photoUrl) {
                      if (photoUrl.indexOf('X-Amz-Signature=') >= 0) {
                        photoUrl = queryString.parseUrl(photoUrl).url;
                      }
                    }

                    return {
                      ...placeholder,
                      value: photoUrl,
                      mediumId,
                      photo_type: medium.mimeType,
                    };
                  }
                }

                return placeholder;
              }),
            };
          }

          return page;
        };

        if (productData.cover) {
          draft.cover = convertPageData(productData.cover);
        }
        if (productData.pages) {
          draft.pages = productData.pages.map(convertPageData);
        }
      }),
    };
  },

  async getSavedCards() {
    try {
      const response = await apiClientGetRequest('api/v1/users/me/cards', {
        Authorization: `Token ${accessToken}`,
      });

      const json = await response.json();

      const data = json.data
        .map(card => ({
          id: card.id,
          expired: card.expired,
          default: card.default,
          brand: card.info.brand,
          name: card.info.name,
          expiration: `${card.info.exp_month}/${card.info.exp_year}`,
          last4: card.info.last4,
          number: `**** **** **** ${card.info.last4}`,
          logo: `card-${card.info.brand.toLowerCase().replace(' ', '-')}.png`,
        }))
        .sort((a, b) => b.default - a.defalt);

      return { data };
    } catch (e) {
      return { error: e.code };
    }
  },

  async removeSavedCard(cardId) {
    try {
      const response = await apiClientDeleteRequest(`api/v1/users/me/cards/${cardId}`, {
        Authorization: `Token ${accessToken}`,
      });

      const json = await response.json();

      return { data: json };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * Adding new provider to current Account
   *
   * @param {object} provider
   */
  async addProvider(provider) {
    try {
      const response = await apiClientPostRequest(
        'api/v1/users/me/providers',
        {
          provider,
        },
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      await response.json();

      return { data: true };
    } catch (e) {
      if (e.code === 400 && e.body === 'Credentials are already used') {
        return {
          error:
            'We’re sorry but the account you are trying to use has already been associated with another account.',
        };
      }
      return { error: e.body };
    }
  },

  /**
   * @param {number} type provider type
   * @param {string} remoteId remote_id
   */
  async removeProvider(type, remoteId) {
    try {
      const response = await apiClientDeleteRequest(
        'api/v1/users/me/providers',
        {
          Authorization: `Token ${accessToken}`,
        },
        {
          provider: type,
          remote_id: remoteId,
        },
      );

      await response.json();

      return { data: true };
    } catch (e) {
      return { error: e.message };
    }
  },
});

export default usersApi;
